import { useState } from 'react';
import { motion } from 'motion/react';
import { cn } from '@/lib/utils';
import SEO from '../components/SEO';

interface FormData {
  tournamentSelection: string;
  schoolName: string;
  headCoachName: string;
  teamAddress: string;
  city: string;
  state: string;
  zipCode: string;
  coachEmail: string;
  coachPhone: string;
  boysCount: number;
  girlsCount: number;
  dualMeetNightBefore: boolean;
}


// Early-bird pricing applies to payments made on or before January 1st, 2027.
// After that date the late schedule kicks in automatically.
const LATE_FEE_START = new Date(2027, 0, 2); // Jan 2, 2027, local time
const isLateRegistration = () => new Date() >= LATE_FEE_START;

// Each tournament's entry fee covers 14 wrestlers of that gender. Jim Root is
// the boys tournament, Kusumoto Klassic is the girls tournament, so a school
// entering both brings two separate rosters that are counted and charged
// independently.
const COVERED_WRESTLERS = 14;

const PRICING = {
  jimroot: {
    label: 'Jim Root Memorial',
    individual: 50,
    teamEarly: 425,
    teamLate: 450,
    extra: 25,
  },
  kusumoto: {
    label: 'Kusumoto Klassic',
    individual: 45,
    teamEarly: 400,
    teamLate: 400,
    extra: 20,
  },
  both: {
    label: 'Both Tournaments',
    individual: 0,
    teamEarly: 725,
    teamLate: 750,
    // The combined package covers 14 boys AND 14 girls. Wrestlers past 14 on
    // each roster are billed at that tournament's own extras rate, so there is
    // no single "extra" figure for the combo -- see calculateFees.
    extra: 0,
  },
} as const;

// Which roster count each option bills against.
const COUNTS_BOYS = (selection: string) => selection === 'jimroot' || selection === 'both';
const COUNTS_GIRLS = (selection: string) => selection === 'kusumoto' || selection === 'both';

interface WrestlerCounterProps {
  label: string;
  accent: string;
  value: number;
  onChange: (value: number) => void;
  error?: string;
}

function WrestlerCounter({ label, accent, value, onChange, error }: WrestlerCounterProps) {
  return (
    <div>
      <label className={cn("block text-lg font-medium mb-3", accent)}>
        {label}
      </label>
      <div className="flex items-center space-x-4">
        <button
          type="button"
          onClick={() => onChange(Math.max(0, value - 1))}
          className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg text-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={value <= 0}
          aria-label={`Decrease ${label}`}
        >
          -
        </button>
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Math.max(0, parseInt(e.target.value) || 0))}
          className="w-24 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-center text-xl font-semibold focus:ring-2 focus:ring-red-500 focus:border-transparent"
          min="0"
          aria-label={label}
        />
        <button
          type="button"
          onClick={() => onChange(value + 1)}
          className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg text-xl font-semibold transition-colors"
          aria-label={`Increase ${label}`}
        >
          +
        </button>
      </div>
      {error && <p className="text-red-500 mt-2">{error}</p>}
    </div>
  );
}

function RegistrationPage() {
  const isLate = isLateRegistration();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    tournamentSelection: '',
    schoolName: '',
    headCoachName: '',
    teamAddress: '',
    city: '',
    state: '',
    zipCode: '',
    coachEmail: '',
    coachPhone: '',
    boysCount: 0,
    girlsCount: 0,
    dualMeetNightBefore: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const totalWrestlers = formData.boysCount + formData.girlsCount;
  // The roster the individual-vs-team threshold is measured against. For a
  // single tournament that is just that tournament's roster; "both" is always
  // team-priced, so the value is unused there.
  const billedCount = formData.tournamentSelection === 'kusumoto'
    ? formData.girlsCount
    : formData.boysCount;

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string) => {
    const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
    return phoneRegex.test(phone);
  };

  const validateStep = (step: number) => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 1:
        if (!formData.tournamentSelection) {
          newErrors.tournamentSelection = 'Please select a tournament option';
        }
        break;
      case 2:
        if (!formData.schoolName) newErrors.schoolName = 'School name is required';
        if (!formData.headCoachName) newErrors.headCoachName = 'Head coach name is required';
        if (!formData.teamAddress) newErrors.teamAddress = 'Team address is required';
        if (!formData.city) newErrors.city = 'City is required';
        if (!formData.state) newErrors.state = 'State is required';
        if (!formData.zipCode) newErrors.zipCode = 'Zip code is required';
        if (!formData.coachEmail) {
          newErrors.coachEmail = 'Coach email is required';
        } else if (!validateEmail(formData.coachEmail)) {
          newErrors.coachEmail = 'Please enter a valid email address';
        }
        if (!formData.coachPhone) {
          newErrors.coachPhone = 'Coach phone is required';
        } else if (!validatePhone(formData.coachPhone)) {
          newErrors.coachPhone = 'Please enter a valid phone number';
        }
        break;
      case 3: {
        const selection = formData.tournamentSelection;
        if (COUNTS_BOYS(selection) && formData.boysCount < 1) {
          newErrors.boysCount = selection === 'both'
            ? 'Enter at least one boy for the Jim Root Memorial'
            : 'Please specify at least one wrestler';
        }
        if (COUNTS_GIRLS(selection) && formData.girlsCount < 1) {
          newErrors.girlsCount = selection === 'both'
            ? 'Enter at least one girl for the Kusumoto Klassic'
            : 'Please specify at least one wrestler';
        }
        break;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };


  const extrasBeyondCoverage = (count: number, rate: number) =>
    Math.max(0, count - COVERED_WRESTLERS) * rate;

  const calculateFees = () => {
    const selection = formData.tournamentSelection as keyof typeof PRICING;
    const pricing = PRICING[selection];
    if (!pricing) {
      return {
        baseFee: 0,
        extraWrestlerFee: 0,
        boysExtraFee: 0,
        girlsExtraFee: 0,
        total: 0,
        isLate,
      };
    }

    const { boysCount, girlsCount } = formData;
    const teamFee = isLate ? pricing.teamLate : pricing.teamEarly;

    let baseFee = 0;
    let boysExtraFee = 0;
    let girlsExtraFee = 0;

    if (selection === 'both') {
      // One combined package fee covering 14 boys and 14 girls. Each roster is
      // billed for its own overage at its own tournament's rate.
      baseFee = teamFee;
      boysExtraFee = extrasBeyondCoverage(boysCount, PRICING.jimroot.extra);
      girlsExtraFee = extrasBeyondCoverage(girlsCount, PRICING.kusumoto.extra);
    } else {
      const count = selection === 'jimroot' ? boysCount : girlsCount;
      if (count >= 6) {
        baseFee = teamFee;
        const overage = extrasBeyondCoverage(count, pricing.extra);
        if (selection === 'jimroot') {
          boysExtraFee = overage;
        } else {
          girlsExtraFee = overage;
        }
      } else {
        baseFee = count * pricing.individual;
      }
    }

    const extraWrestlerFee = boysExtraFee + girlsExtraFee;

    return {
      baseFee,
      extraWrestlerFee,
      boysExtraFee,
      girlsExtraFee,
      total: baseFee + extraWrestlerFee,
      isLate,
    };
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setIsSubmitting(true);
    setSubmitError(null);

    // Generate unique registration ID
    const registrationId = `REG-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const submissionData = {
      ...formData,
      // Total across both rosters. Kept alongside the split counts so the
      // spreadsheet's existing "Wrestler Count" column stays meaningful.
      wrestlerCount: formData.boysCount + formData.girlsCount,
      registrationId,
      submittedAt: new Date().toISOString(),
      fees: calculateFees(),
    };

    try {
      // Get the Google Apps Script Web App URL from environment variable
      const webAppUrl = import.meta.env.VITE_GOOGLE_SHEETS_URL;

      if (!webAppUrl) {
        throw new Error('Google Sheets integration not configured. Please contact the tournament organizers.');
      }

      // Submit to Google Sheets
      await fetch(webAppUrl, {
        method: 'POST',
        mode: 'no-cors', // Required for Google Apps Script
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      // Note: With no-cors mode, we can't read the response, but the request will succeed
      // Show success message
      const rosterSummary = [
        COUNTS_BOYS(formData.tournamentSelection) ? `Boys (Jim Root): ${formData.boysCount}` : null,
        COUNTS_GIRLS(formData.tournamentSelection) ? `Girls (Kusumoto): ${formData.girlsCount}` : null,
      ].filter(Boolean).join('\n');

      alert(
        `Registration submitted successfully!\n\n` +
        `Registration ID: ${registrationId}\n\n` +
        `A confirmation email has been sent to ${formData.coachEmail}\n\n` +
        `${rosterSummary}\n\n` +
        `Payment Instructions:\n` +
        `Make checks payable to: Prospect HS Wrestling\n` +
        `C/O Ernie Vasheresse, 18900 Prospect Rd, Saratoga, CA 95070\n\n` +
        `Total Amount Due: $${calculateFees().total}`
      );

      // Redirect to home page
      window.location.href = '/';

    } catch (error) {
      console.error('Registration error:', error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'Failed to submit registration. Please try again or contact us at Jimrootwrestling@gmail.com'
      );
      setIsSubmitting(false);
    }
  };

  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3].map((step) => (
        <div key={step} className="flex items-center">
          <div
            className={cn(
              "w-10 h-10 rounded-full flex items-center justify-center font-bold transition-colors",
              currentStep >= step
                ? "bg-red-600 text-white"
                : "bg-gray-800 text-gray-400"
            )}
          >
            {step}
          </div>
          {step < 3 && (
            <div
              className={cn(
                "w-20 h-1 transition-colors",
                currentStep > step ? "bg-red-600" : "bg-gray-800"
              )}
            />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <>
      <SEO
        title="Tournament Registration"
        description="Register your team for the 63rd Annual Jim Root Memorial and 10th Annual Kusumoto Klassic wrestling tournaments at Prospect High School. Online registration available now!"
        keywords={[
          'wrestling tournament registration',
          'Jim Root registration',
          'Kusumoto Klassic registration',
          'high school wrestling registration',
          'Prospect High School wrestling',
          'Bay Area wrestling registration',
          'California wrestling tournament'
        ]}
        canonical="https://jimrootwrestling.com/register"
      />
      <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="bg-black/50 backdrop-blur-md border-b border-gray-800 py-4">
        <div className="container mx-auto px-6">
          <a href="/" className="text-xl font-bold bg-gradient-to-r from-red-500 to-yellow-500 bg-clip-text text-transparent">
            Jim Root Memorial
          </a>
        </div>
      </header>

      {/* Registration Form */}
      <div className="container mx-auto max-w-4xl px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl font-bold text-center mb-2">Tournament Registration</h1>
          <p className="text-gray-400 text-center mb-8">
            Register your team for the Jim Root Memorial and/or Kusumoto Klassic
          </p>

          <StepIndicator />

          <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-800">
            {/* Step 1: Tournament Selection */}
            {currentStep === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-2xl font-semibold mb-6">Step 1: Select Tournament</h2>

                <div className={cn(
                  "mb-6 rounded-lg border p-4 text-sm",
                  isLate
                    ? "bg-orange-900/30 border-orange-700 text-orange-200"
                    : "bg-green-900/30 border-green-700 text-green-200"
                )}>
                  {isLate
                    ? "Late registration pricing is in effect (payments after January 1st, 2027)."
                    : "Early registration pricing is in effect. Pay by January 1st, 2027 to lock in these rates."}
                </div>

                <div className="space-y-4">
                  <label className="block">
                    <input
                      type="radio"
                      name="tournament"
                      value="jimroot"
                      checked={formData.tournamentSelection === 'jimroot'}
                      onChange={(e) => setFormData({ ...formData, tournamentSelection: e.target.value })}
                      className="mr-3"
                    />
                    <span className="text-lg">Jim Root Memorial only</span>
                    <span className="text-gray-400 ml-2">
                      (${isLate ? PRICING.jimroot.teamLate : PRICING.jimroot.teamEarly} team / ${PRICING.jimroot.individual} individual - Boys Tournament, Sat Jan 16, 2027)
                    </span>
                  </label>
                  <label className="block">
                    <input
                      type="radio"
                      name="tournament"
                      value="kusumoto"
                      checked={formData.tournamentSelection === 'kusumoto'}
                      onChange={(e) => setFormData({ ...formData, tournamentSelection: e.target.value })}
                      className="mr-3"
                    />
                    <span className="text-lg">Kusumoto Klassic only</span>
                    <span className="text-gray-400 ml-2">(${PRICING.kusumoto.teamEarly} team / ${PRICING.kusumoto.individual} individual - Girls Tournament, Fri Jan 15, 2027)</span>
                  </label>
                  <label className="block">
                    <input
                      type="radio"
                      name="tournament"
                      value="both"
                      checked={formData.tournamentSelection === 'both'}
                      onChange={(e) => setFormData({ ...formData, tournamentSelection: e.target.value })}
                      className="mr-3"
                    />
                    <span className="text-lg">Both tournaments</span>
                    <span className="text-gray-400 ml-2">
                      (${isLate ? PRICING.both.teamLate : PRICING.both.teamEarly} - Jim Root + Kusumoto Klassic)
                    </span>
                  </label>
                </div>
                {errors.tournamentSelection && (
                  <p className="text-red-500 mt-2">{errors.tournamentSelection}</p>
                )}

                <div className="mt-6 pt-6 border-t border-gray-800">
                  <p className="text-sm text-gray-400 mb-3">Download the tournament fliers:</p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="/fliers/jim-root-2027.pdf"
                      download
                      className="inline-flex items-center gap-2 bg-blue-600/80 hover:bg-blue-600 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                    >
                      Jim Root Memorial 2027 (PDF)
                    </a>
                    <a
                      href="/fliers/kusumoto-klassic-2027.pdf"
                      download
                      className="inline-flex items-center gap-2 bg-pink-600/80 hover:bg-pink-600 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                    >
                      Kusumoto Klassic 2027 (PDF)
                    </a>
                  </div>
                </div>
                <div className="mt-8 flex justify-end">
                  <button
                    onClick={handleNext}
                    className="bg-red-600 hover:bg-red-700 px-6 py-2 rounded-full font-semibold transition-colors"
                  >
                    Next Step
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Team Information */}
            {currentStep === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-2xl font-semibold mb-6">Step 2: Team Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      School Name *
                    </label>
                    <input
                      type="text"
                      value={formData.schoolName}
                      onChange={(e) => setFormData({ ...formData, schoolName: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                    {errors.schoolName && (
                      <p className="text-red-500 text-sm mt-1">{errors.schoolName}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Head Coach Name *
                    </label>
                    <input
                      type="text"
                      value={formData.headCoachName}
                      onChange={(e) => setFormData({ ...formData, headCoachName: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                    {errors.headCoachName && (
                      <p className="text-red-500 text-sm mt-1">{errors.headCoachName}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Team Address *
                    </label>
                    <input
                      type="text"
                      value={formData.teamAddress}
                      onChange={(e) => setFormData({ ...formData, teamAddress: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                    {errors.teamAddress && (
                      <p className="text-red-500 text-sm mt-1">{errors.teamAddress}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                    {errors.city && (
                      <p className="text-red-500 text-sm mt-1">{errors.city}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      State *
                    </label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                    {errors.state && (
                      <p className="text-red-500 text-sm mt-1">{errors.state}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Zip Code *
                    </label>
                    <input
                      type="text"
                      value={formData.zipCode}
                      onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                    {errors.zipCode && (
                      <p className="text-red-500 text-sm mt-1">{errors.zipCode}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Coach Email *
                    </label>
                    <input
                      type="email"
                      value={formData.coachEmail}
                      onChange={(e) => setFormData({ ...formData, coachEmail: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                    {errors.coachEmail && (
                      <p className="text-red-500 text-sm mt-1">{errors.coachEmail}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Coach Cell Phone *
                    </label>
                    <input
                      type="tel"
                      value={formData.coachPhone}
                      onChange={(e) => setFormData({ ...formData, coachPhone: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                    {errors.coachPhone && (
                      <p className="text-red-500 text-sm mt-1">{errors.coachPhone}</p>
                    )}
                  </div>
                </div>
                <div className="mt-8 flex justify-between">
                  <button
                    onClick={handlePrevious}
                    className="bg-gray-700 hover:bg-gray-600 px-6 py-2 rounded-full font-semibold transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    onClick={handleNext}
                    className="bg-red-600 hover:bg-red-700 px-6 py-2 rounded-full font-semibold transition-colors"
                  >
                    Next Step
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Number of Wrestlers */}
            {currentStep === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-2xl font-semibold mb-6">Step 3: Number of Wrestlers</h2>

                {/* Dynamic pricing display based on tournament selection */}
                <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold mb-4">
                    Pricing Information
                    <span className={cn("ml-2 text-xs font-medium", isLate ? "text-orange-400" : "text-green-400")}>
                      {isLate ? '(late rates — after Jan 1, 2027)' : '(early rates — pay by Jan 1, 2027)'}
                    </span>
                  </h3>
                  {formData.tournamentSelection === 'jimroot' && (
                    <div className="space-y-2 text-sm">
                      <p>Individual Registration (1-5 wrestlers): <span className="font-semibold">${PRICING.jimroot.individual} per wrestler</span></p>
                      <p>Team Registration (6-14 wrestlers): <span className="font-semibold">${isLate ? PRICING.jimroot.teamLate : PRICING.jimroot.teamEarly}</span></p>
                      <p>Team with 15+ wrestlers: <span className="font-semibold">${isLate ? PRICING.jimroot.teamLate : PRICING.jimroot.teamEarly} + ${PRICING.jimroot.extra} per additional wrestler</span></p>
                    </div>
                  )}
                  {formData.tournamentSelection === 'kusumoto' && (
                    <div className="space-y-2 text-sm">
                      <p>Individual Registration (1-5 wrestlers): <span className="font-semibold">${PRICING.kusumoto.individual} per wrestler</span></p>
                      <p>Team Registration (6-14 wrestlers): <span className="font-semibold">${PRICING.kusumoto.teamEarly}</span></p>
                      <p>Team with 15+ wrestlers: <span className="font-semibold">${PRICING.kusumoto.teamEarly} + ${PRICING.kusumoto.extra} per additional wrestler</span></p>
                    </div>
                  )}
                  {formData.tournamentSelection === 'both' && (
                    <div className="space-y-2 text-sm">
                      <p>Combined Registration: <span className="font-semibold">${isLate ? PRICING.both.teamLate : PRICING.both.teamEarly}</span></p>
                      <p className="text-gray-400">Covers up to {COVERED_WRESTLERS} boys <span className="text-gray-500">and</span> {COVERED_WRESTLERS} girls — the two rosters are counted separately.</p>
                      <p>Additional boys (past {COVERED_WRESTLERS}): <span className="font-semibold">${PRICING.jimroot.extra} each</span></p>
                      <p>Additional girls (past {COVERED_WRESTLERS}): <span className="font-semibold">${PRICING.kusumoto.extra} each</span></p>
                    </div>
                  )}
                  <p className="mt-4 text-xs text-yellow-400 font-semibold">
                    Team Champions get free entry into next year's tournament.
                  </p>
                </div>

                <div className="mb-6 space-y-6">
                  {COUNTS_BOYS(formData.tournamentSelection) && (
                    <WrestlerCounter
                      label={
                        formData.tournamentSelection === 'both'
                          ? 'Boys — Jim Root Memorial (Sat Jan 16)'
                          : 'How many wrestlers are you registering?'
                      }
                      accent="text-blue-400"
                      value={formData.boysCount}
                      onChange={(boysCount) => setFormData({ ...formData, boysCount })}
                      error={errors.boysCount}
                    />
                  )}
                  {COUNTS_GIRLS(formData.tournamentSelection) && (
                    <WrestlerCounter
                      label={
                        formData.tournamentSelection === 'both'
                          ? 'Girls — Kusumoto Klassic (Fri Jan 15)'
                          : 'How many wrestlers are you registering?'
                      }
                      accent="text-pink-400"
                      value={formData.girlsCount}
                      onChange={(girlsCount) => setFormData({ ...formData, girlsCount })}
                      error={errors.girlsCount}
                    />
                  )}
                </div>

                {/* Live fee calculation */}
                {totalWrestlers > 0 && (() => {
                  const fees = calculateFees();
                  const isTeamRate = formData.tournamentSelection === 'both' || billedCount >= 6;
                  const extraBoys = Math.max(0, formData.boysCount - COVERED_WRESTLERS);
                  const extraGirls = Math.max(0, formData.girlsCount - COVERED_WRESTLERS);
                  return (
                    <div className="bg-gray-800/50 rounded-lg p-4 mb-6">
                      <h4 className="font-semibold mb-2">Registration Fee Calculation:</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-400">{isTeamRate ? 'Team fee' : 'Individual fee'}:</span>
                          <span>${fees.baseFee}</span>
                        </div>
                        {COUNTS_BOYS(formData.tournamentSelection) && extraBoys > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-400">
                              Additional boys ({extraBoys} &times; ${PRICING.jimroot.extra}):
                            </span>
                            <span>${fees.boysExtraFee}</span>
                          </div>
                        )}
                        {COUNTS_GIRLS(formData.tournamentSelection) && extraGirls > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-400">
                              Additional girls ({extraGirls} &times; ${PRICING.kusumoto.extra}):
                            </span>
                            <span>${fees.girlsExtraFee}</span>
                          </div>
                        )}
                        <div className="border-t border-gray-700 pt-2 mt-2 flex justify-between text-lg font-semibold">
                          <span>Total:</span>
                          <span className="text-red-500">${fees.total}</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Payment Instructions */}
                <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold mb-2 text-blue-300">Payment Instructions</h3>
                  <p className="text-gray-300 mb-2">
                    Make checks payable to <span className="font-semibold text-white">Prospect HS Wrestling</span>
                  </p>
                  <p className="text-gray-400 text-sm">
                    C/O Ernie Vasheresse<br />
                    18900 Prospect Rd<br />
                    Saratoga, CA 95070
                  </p>
                </div>

                {/* Special Circumstances */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3">Special Circumstances</h3>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.dualMeetNightBefore}
                      onChange={(e) => setFormData({ ...formData, dualMeetNightBefore: e.target.checked })}
                      className="mr-3"
                    />
                    <span>Team has a dual meet the night before (affects weigh-in allowance)</span>
                  </label>
                </div>

                {/* Registration Summary */}
                <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold mb-3">Registration Summary</h3>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-gray-400">School:</span> {formData.schoolName}</p>
                    <p><span className="text-gray-400">Coach:</span> {formData.headCoachName}</p>
                    <p><span className="text-gray-400">Email:</span> {formData.coachEmail}</p>
                    {COUNTS_BOYS(formData.tournamentSelection) && (
                      <p><span className="text-gray-400">Boys (Jim Root):</span> {formData.boysCount}</p>
                    )}
                    {COUNTS_GIRLS(formData.tournamentSelection) && (
                      <p><span className="text-gray-400">Girls (Kusumoto):</span> {formData.girlsCount}</p>
                    )}
                    {formData.tournamentSelection === 'both' && (
                      <p><span className="text-gray-400">Total wrestlers:</span> {totalWrestlers}</p>
                    )}
                    <p><span className="text-gray-400">Tournament:</span> {
                      PRICING[formData.tournamentSelection as keyof typeof PRICING]?.label ?? '—'
                    }</p>
                  </div>
                </div>

                {submitError && (
                  <div className="mb-4 p-4 bg-red-900/30 border border-red-700 rounded-lg text-red-300">
                    <p className="font-semibold">Error submitting registration:</p>
                    <p>{submitError}</p>
                  </div>
                )}

                <div className="mt-8 flex justify-between">
                  <button
                    onClick={handlePrevious}
                    disabled={isSubmitting}
                    className="bg-gray-700 hover:bg-gray-600 px-6 py-2 rounded-full font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="bg-gradient-to-r from-red-600 to-yellow-600 hover:from-red-700 hover:to-yellow-700 px-8 py-2 rounded-full font-semibold transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Registration'}
                  </button>
                </div>
              </motion.div>
            )}

          </div>
        </motion.div>
      </div>
    </div>
    </>
  );
}

export default RegistrationPage;